// src/components/player/PlayerHistoricalAnalytics.js
import React, { useState, useEffect } from 'react';
import { 
  Calendar, Clock, BarChart3, TrendingUp, TrendingDown,
  Award, ChevronRight, GitCompare, History, LineChart
} from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import { formatBattingAvg, formatERA } from '../../services/statFormatUtils';
import { DynastyTable } from '../../services/tableService';
import { playersAPI } from '../../services/apiService';

const PlayerHistoricalAnalytics = ({
  player,
  playerId,
  leagueId = null,
  playerName,
  isPitcher = false
}) => {
  const [selectedView, setSelectedView] = useState('career');
  const [historicalData, setHistoricalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch historical data on mount
  useEffect(() => {
    const fetchHistoricalData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await playersAPI.getPlayerHistorical(playerId, leagueId);
        
        if (response.success) {
          setHistoricalData(response);
        } else {
          setError(response.error || 'Failed to load historical data');
        }
      } catch (err) {
        console.error('Error fetching historical data:', err);
        setError('Failed to load historical data');
      } finally {
        setLoading(false);
      }
    };

    if (playerId) {
      fetchHistoricalData();
    }
  }, [playerId, leagueId]);

  // Career data from player prop (modal), historical-only data from /historical response
  const career_stats = player?.career_stats || [];
  const career_totals = historicalData?.career_totals || null;
  const monthly_splits = historicalData?.monthly || [];
  const year_over_year = historicalData?.year_over_year || [];
  const milestones = historicalData?.milestones || [];

  // Month names
  const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Calculate OPS for monthly splits
  const calculateOPS = (obp, slg) => {
    return formatBattingAvg(parseFloat(obp || 0) + parseFloat(slg || 0));
  };

  // Create monthly performance columns with OPS
  const createMonthlyColumns = () => {
    if (isPitcher) {
      return [
        { key: 'month', title: 'Month', width: 80, render: (player) => monthNames[player.month] || '-' },
        { key: 'games', title: 'G', width: 50, render: (player) => player?.games || 0 },
        { key: 'wins', title: 'W', width: 50, render: (player) => player?.wins || 0 },
        { key: 'era', title: 'ERA', width: 70, render: (player) => formatERA(player?.era) },
        { key: 'whip', title: 'WHIP', width: 70, render: (player) => formatBattingAvg(player?.whip) },
        { key: 'strikeouts', title: 'K', width: 50, render: (player) => player?.strikeouts || 0 },
        { key: 'innings_pitched', title: 'IP', width: 60, render: (player) => player?.innings_pitched?.toFixed(1) || '-' }
      ];
    }
    
    return [
    { key: 'month', title: 'Month', width: 80, render: (player) => monthNames[player.month] || '-' },
    { key: 'games', title: 'G', width: 50, render: (player) => player?.games || 0 },
    { key: 'batting_avg', title: 'AVG', width: 70, render: (player) => formatBattingAvg(player?.batting_avg) },
    { key: 'obp', title: 'OBP', width: 70, render: (player) => formatBattingAvg(player?.obp) },
    {
    key: 'ops',
    title: 'OPS',
    width: 70,
    render: (player) => {
      // Phase 1.2 truthfulness fix (2026-05-20, Rule 9): do NOT synthesize
      // SLG from AVG × 1.5 — that fabricates a number the source doesn't
      // contain. If OBP or SLG is missing from the monthly split row, show
      // an em-dash rather than computing OPS from invented values.
      const obp = player?.obp;
      const slg = player?.slg;
      if (obp == null || slg == null) return '—';
      return calculateOPS(obp, slg);
    }
    },
    { key: 'home_runs', title: 'HR', width: 50, render: (player) => player?.home_runs || 0 },
    { key: 'rbi', title: 'RBI', width: 50, render: (player) => player?.rbi || 0 },
    { key: 'stolen_bases', title: 'SB', width: 50, render: (player) => player?.stolen_bases || 0 }
    ];
  };

  // Create historical career columns
  const createCareerColumns = () => {
    if (isPitcher) {
      return [
        { key: 'season', title: 'Year', width: 60 },
        { key: 'mlb_team', title: 'Team', width: 60 },
        {
        key: 'games_played',
        title: 'G',
        width: 50,
        render: (player) => player?.pitching?.games_played || 0
        },
        {
        key: 'games_started',
        title: 'GS',
        width: 50,
        render: (player) => player?.pitching?.games_started || 0
        },
        {
        key: 'wins',
        title: 'W',
        width: 40,
        render: (player) => player?.pitching?.wins || 0
        },
        {
        key: 'losses',
        title: 'L',
        width: 40,
        render: (player) => player?.pitching?.losses || 0
        },
        {
        key: 'innings_pitched',
        title: 'IP',
        width: 60,
        render: (player) => player?.pitching?.innings_pitched?.toFixed(1) || '-'
        },
        {
        key: 'era',
        title: 'ERA',
        width: 60,
        render: (player) => formatERA(player?.pitching?.era)
        },
        {
        key: 'whip',
        title: 'WHIP',
        width: 60,
        render: (player) => formatBattingAvg(player?.pitching?.whip)
        },
        {
        key: 'strikeouts_pitched',
        title: 'K',
        width: 50,
        render: (player) => player?.pitching?.strikeouts_pitched || 0
        },
        {
        key: 'hits_allowed',
        title: 'H',
        width: 50,
        render: (player) => player?.pitching?.hits_allowed || 0
        },
        {
        key: 'walks_allowed',
        title: 'BB',
        width: 50,
        render: (player) => player?.pitching?.walks_allowed || 0
        },
        {
        key: 'saves',
        title: 'SV',
        width: 40,
        render: (player) => player?.pitching?.saves || 0
        },
        {
        key: 'quality_starts',
        title: 'QS',
        width: 40,
        render: (player) => player?.pitching?.quality_starts || 0
        }
      ];
    }
    
    return [
      { key: 'season', title: 'Year', width: 60 },
      { key: 'mlb_team', title: 'Team', width: 60 },
      {
      key: 'games_played',
      title: 'G',
      width: 50,
      render: (player) => player?.hitting?.games_played || player?.pitching?.games_played || 0
      },
      {
      key: 'at_bats',
      title: 'AB',
      width: 50,
      render: (player) => player?.hitting?.at_bats || 0
      },
      {
      key: 'batting_avg',
      title: 'AVG',
      width: 60,
      render: (player) => formatBattingAvg(player?.hitting?.batting_avg)
      },
      {
      key: 'obp',
      title: 'OBP',
      width: 60,
      render: (player) => formatBattingAvg(player?.hitting?.obp)
      },
      {
      key: 'slg',
      title: 'SLG',
      width: 60,
      render: (player) => formatBattingAvg(player?.hitting?.slg)
      },
      {
      key: 'ops',
      title: 'OPS',
      width: 60,
      render: (player) => formatBattingAvg(player?.hitting?.ops)
      },
      {
      key: 'home_runs',
      title: 'HR',
      width: 40,
      render: (player) => player?.hitting?.home_runs || 0
      },
      {
      key: 'rbi',
      title: 'RBI',
      width: 50,
      render: (player) => player?.hitting?.rbi || 0
      },
      {
      key: 'stolen_bases',
      title: 'SB',
      width: 40,
      render: (player) => player?.hitting?.stolen_bases || 0
      },
      {
      key: 'runs',
      title: 'R',
      width: 40,
      render: (player) => player?.hitting?.runs || 0
      }
    ];
  };

  // Show loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className={`${dynastyTheme.components.card.base} p-12 rounded-lg text-center`}>
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className={dynastyTheme.classes.text.neutralLight}>Loading historical data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className={`${dynastyTheme.components.card.base} p-12 rounded-lg text-center`}>
          <p className={dynastyTheme.classes.text.error}>{error}</p>
        </div>
      </div>
    );
  }

  // Calculate career high for a stat
  const getCareerHigh = (stat) => {
    if (!career_stats || career_stats.length === 0) return null;
    
    // Determine if this is a hitting or pitching stat
    const isHittingStat = ['home_runs', 'rbi', 'batting_avg', 'stolen_bases', 'runs', 'hits'].includes(stat);
    const isPitchingStat = ['wins', 'strikeouts_pitched', 'era', 'saves', 'quality_starts'].includes(stat);
    
    // Extract values from nested structure
    const values = career_stats.map(season => {
      if (isHittingStat) {
        return season?.hitting?.[stat] || 0;
      } else if (isPitchingStat) {
        return season?.pitching?.[stat] || 0;
      }
      return 0;
    });
    
    const max = Math.max(...values);
    
    // Find season with max value
    const season = career_stats.find(s => {
      if (isHittingStat) {
        return s?.hitting?.[stat] === max;
      } else if (isPitchingStat) {
        return s?.pitching?.[stat] === max;
      }
      return false;
    });
    
    return { value: max, year: season?.season };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${dynastyTheme.components.card.highlighted} p-6 rounded-lg`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className={`text-xl font-bold ${dynastyTheme.classes.text.white} mb-2 flex items-center`}>
              <History className={`w-5 h-5 mr-2 ${dynastyTheme.classes.text.primary}`} />
              Historical Analytics & Career Progression
            </h3>
            <p className={dynastyTheme.classes.text.neutralLight}>
              Complete career history, trends, and year-over-year analysis
            </p>
          </div>
        </div>
      </div>

      {/* View Selector */}
      <div className="flex gap-2">
        {['career', 'monthly', 'milestones', 'progression'].map(view => (
          <button
            key={view}
            onClick={() => setSelectedView(view)}
            className={`px-4 py-2 rounded-md font-medium capitalize transition-all ${
              selectedView === view
                ? `${dynastyTheme.classes.bg.primary} text-black`
                : `${dynastyTheme.classes.bg.darkLighter} ${dynastyTheme.classes.text.neutralLight} hover:text-white`
            }`}
          >
            {view}
          </button>
        ))}
      </div>

      {selectedView === 'career' && (
        <>
          {/* Career Summary Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className={`${dynastyTheme.components.card.interactive} p-4 rounded-lg`}>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-2`}>Career Games</div>
              <div className={`text-3xl font-bold ${dynastyTheme.classes.text.white}`}>
                {career_totals?.hitting?.games_played || career_stats?.reduce((sum, s) => sum + (s.hitting?.games_played || 0), 0) || 0}
              </div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutral} mt-1`}>
                {career_stats?.length || 0} seasons
              </div>
            </div>
            
            <div className={`${dynastyTheme.components.card.interactive} p-4 rounded-lg`}>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                Career {isPitcher ? 'ERA' : 'AVG'}
              </div>
              <div className={`text-3xl font-bold ${dynastyTheme.classes.text.primary}`}>
                {isPitcher
                  ? (formatERA(career_totals?.pitching?.era) || '-')
                  : (formatBattingAvg(career_totals?.hitting?.batting_avg) || '.000')}
              </div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutral} mt-1`}>
                Lifetime
              </div>
            </div>
            
            <div className={`${dynastyTheme.components.card.interactive} p-4 rounded-lg`}>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                Career {isPitcher ? 'Strikeouts' : 'Home Runs'}
              </div>
              <div className={`text-3xl font-bold ${dynastyTheme.classes.text.warning}`}>
                {isPitcher 
                  ? (career_totals?.pitching?.strikeouts_pitched || 0)
                  : (career_totals?.hitting?.home_runs || 0)}
              </div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutral} mt-1`}>
                Total
              </div>
            </div>
            
            <div className={`${dynastyTheme.components.card.interactive} p-4 rounded-lg`}>
              <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                Career {isPitcher ? 'Wins' : 'RBI'}
              </div>
              <div className={`text-3xl font-bold ${dynastyTheme.classes.text.success}`}>
                {isPitcher 
                  ? (career_totals?.pitching?.wins || 0)
                  : (career_totals?.hitting?.rbi || 0)}
              </div>
              <div className={`text-xs ${dynastyTheme.classes.text.neutral} mt-1`}>
                Total
              </div>
            </div>
          </div>

          {/* Complete Career History Table */}
          {career_stats && career_stats.length > 0 && (
            <div className={`${dynastyTheme.components.card.base} p-6 rounded-lg`}>
              <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4 flex items-center`}>
                <Clock className="w-5 h-5 mr-2" />
                Complete Career Statistics
              </h4>
              
              <DynastyTable
                data={[
                  ...career_stats.slice().reverse(), // Reverse to show oldest first
                  ...(career_totals ? [{ ...career_totals, season: 'TOTAL', mlb_team: 'Career' }] : [])
                ]}
                columns={createCareerColumns()}
                maxHeight="500px"
                enableHorizontalScroll={true}
                className="mt-4"
                initialSort={{ key: 'season', direction: 'asc' }}
              />
            </div>
          )}
        </>
      )}

      {selectedView === 'monthly' && (
        <>
          {/* Monthly Performance Table */}
          {monthly_splits && monthly_splits.length > 0 ? (
            <div className={`${dynastyTheme.components.card.base} p-6 rounded-lg`}>
              <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4 flex items-center`}>
                <Calendar className="w-5 h-5 mr-2" />
                Month-by-Month Performance (Current Season)
              </h4>
              
              <DynastyTable
                data={monthly_splits}
                columns={createMonthlyColumns()}
                maxHeight="400px"
                enableHorizontalScroll={false}
                className="mt-4"
              />
            </div>
          ) : (
            <div className={`${dynastyTheme.components.card.base} p-6 rounded-lg`}>
              <div className={`text-center py-12 ${dynastyTheme.classes.text.neutralLight}`}>
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                No monthly data available
              </div>
            </div>
          )}

          {/* Monthly Trends Visualization */}
          <div className={`${dynastyTheme.components.card.interactive} p-6 rounded-lg`}>
            <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4 flex items-center`}>
              <LineChart className="w-5 h-5 mr-2" />
              Seasonal Performance Pattern
            </h4>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {['April/May', 'June/July', 'August', 'Sept/Oct'].map((period, idx) => {
                const performance = ['Starting', 'Peak', 'Consistent', 'Closing'][idx];
                const trend = ['+', '++', '=', '-'][idx];
                
                return (
                  <div key={period} className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-lg text-center`}>
                    <div className={`text-sm ${dynastyTheme.classes.text.neutralLight} mb-2`}>{period}</div>
                    <div className={`text-2xl font-bold ${
                      trend === '++' ? dynastyTheme.classes.text.success :
                      trend === '+' ? dynastyTheme.classes.text.primary :
                      trend === '=' ? dynastyTheme.classes.text.white :
                      dynastyTheme.classes.text.warning
                    }`}>
                      {performance}
                    </div>
                    <div className={`text-xs ${dynastyTheme.classes.text.neutral} mt-1`}>
                      Historical Trend: {trend}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {selectedView === 'milestones' && (
        <>
          {/* Career Highs */}
          <div className={`${dynastyTheme.components.card.highlighted} p-6 rounded-lg`}>
            <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4 flex items-center`}>
              <Award className="w-5 h-5 mr-2" />
              Career Highs & Achievements
            </h4>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {(isPitcher ? 
                ['wins', 'strikeouts_pitched', 'era', 'saves'] :
                ['home_runs', 'rbi', 'batting_avg', 'stolen_bases']
              ).map(stat => {
                const high = getCareerHigh(stat);
                return (
                  <div key={stat} className={`${dynastyTheme.classes.bg.darkLighter} p-4 rounded-lg`}>
                    <div className={`text-xs ${dynastyTheme.classes.text.neutralLight} mb-2 uppercase`}>
                      Best {stat.replace(/_/g, ' ')}
                    </div>
                    <div className={`text-2xl font-bold ${dynastyTheme.classes.text.primary}`}>
                      {stat.includes('avg') ?
                        (formatBattingAvg(high?.value) || '-') :
                        stat === 'era' ?
                        (formatERA(high?.value) || '-') :
                        high?.value || '-'}
                    </div>
                    <div className={`text-xs ${dynastyTheme.classes.text.neutral} mt-1`}>
                      {high?.year || '-'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Career Milestones */}
          <div className={`${dynastyTheme.components.card.base} p-6 rounded-lg`}>
            <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4`}>
              Notable Career Milestones
            </h4>
            
            {milestones && milestones.length > 0 ? (
              <div className="space-y-3">
                {milestones.map(item => (
                <div key={item.date} className={`${dynastyTheme.components.listItem.hoverable} flex items-center justify-between`}>
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{item.icon}</span>
                    <div>
                      <div className={dynastyTheme.classes.text.white}>{item.milestone}</div>
                      <div className={`text-xs ${dynastyTheme.classes.text.neutral}`}>{item.date}</div>
                    </div>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${dynastyTheme.classes.text.neutralLight}`} />
                </div>
              ))}
              </div>
            ) : (
              <div className={`text-center py-8 ${dynastyTheme.classes.text.neutralLight}`}>
                <Award className="w-12 h-12 mx-auto mb-3 opacity-50" />
                No milestones data available
              </div>
            )}
          </div>
        </>
      )}

      {selectedView === 'progression' && (
        <>
          {/* Year-over-Year Comparison */}
          {year_over_year && year_over_year.length > 0 ? (
            <div className={`${dynastyTheme.components.card.base} p-6 rounded-lg`}>
              <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4 flex items-center`}>
                <BarChart3 className="w-5 h-5 mr-2" />
                Year-over-Year Progression
              </h4>
              
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${dynastyTheme.classes.border.neutral}`}>
                      <th className={`text-left py-3 px-4 text-sm ${dynastyTheme.classes.text.primary}`}>Year</th>
                      <th className={`text-center py-3 px-4 text-sm ${dynastyTheme.classes.text.neutralLight}`}>Games</th>
                      {isPitcher ? (
                        <>
                          <th className={`text-center py-3 px-4 text-sm ${dynastyTheme.classes.text.neutralLight}`}>W-L</th>
                          <th className={`text-center py-3 px-4 text-sm ${dynastyTheme.classes.text.neutralLight}`}>ERA Δ</th>
                          <th className={`text-center py-3 px-4 text-sm ${dynastyTheme.classes.text.neutralLight}`}>WHIP Δ</th>
                          <th className={`text-center py-3 px-4 text-sm ${dynastyTheme.classes.text.neutralLight}`}>K/9 Δ</th>
                        </>
                      ) : (
                        <>
                          <th className={`text-center py-3 px-4 text-sm ${dynastyTheme.classes.text.neutralLight}`}>AVG Δ</th>
                          <th className={`text-center py-3 px-4 text-sm ${dynastyTheme.classes.text.neutralLight}`}>OPS Δ</th>
                          <th className={`text-center py-3 px-4 text-sm ${dynastyTheme.classes.text.neutralLight}`}>HR Δ</th>
                          <th className={`text-center py-3 px-4 text-sm ${dynastyTheme.classes.text.neutralLight}`}>RBI Δ</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {year_over_year.map((yoy, idx) => (
                      <tr key={yoy.year} className={`border-b ${dynastyTheme.classes.border.neutral} hover:bg-gray-800/30 transition-colors`}>
                        <td className={`py-3 px-4 font-bold ${dynastyTheme.classes.text.white}`}>
                          {yoy.year}
                        </td>
                        <td className={`text-center py-3 px-4 ${dynastyTheme.classes.text.white}`}>
                          {yoy.games || '-'}
                        </td>
                        {isPitcher ? (
                          <>
                            <td className={`text-center py-3 px-4 ${dynastyTheme.classes.text.white}`}>
                              {yoy.wins}-{yoy.losses}
                            </td>
                            <td className={`text-center py-3 px-4 font-bold ${
                              yoy.era_change <= 0 ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.error
                            }`}>
                              {yoy.era_change > 0 ? '+' : ''}{yoy.era_change?.toFixed(2) || '-'}
                            </td>
                            <td className={`text-center py-3 px-4 font-bold ${
                              yoy.whip_change <= 0 ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.error
                            }`}>
                              {yoy.whip_change > 0 ? '+' : ''}{formatBattingAvg(yoy.whip_change) || '-'}
                            </td>
                            <td className={`text-center py-3 px-4 font-bold ${
                              yoy.k9_change >= 0 ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.error
                            }`}>
                              {yoy.k9_change > 0 ? '+' : ''}{yoy.k9_change?.toFixed(1) || '-'}
                            </td>
                          </>
                        ) : (
                          <>
                            <td className={`text-center py-3 px-4 font-bold ${
                              yoy.avg_change >= 0 ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.error
                            }`}>
                              {yoy.avg_change > 0 ? '+' : ''}{formatBattingAvg(yoy.avg_change) || '-'}
                            </td>
                            <td className={`text-center py-3 px-4 font-bold ${
                              yoy.ops_change >= 0 ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.error
                            }`}>
                              {yoy.ops_change > 0 ? '+' : ''}{formatBattingAvg(yoy.ops_change) || '-'}
                            </td>
                            <td className={`text-center py-3 px-4 font-bold ${
                              yoy.hr_change >= 0 ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.error
                            }`}>
                              {yoy.hr_change > 0 ? '+' : ''}{yoy.hr_change || '-'}
                            </td>
                            <td className={`text-center py-3 px-4 font-bold ${
                              yoy.rbi_change >= 0 ? dynastyTheme.classes.text.success : dynastyTheme.classes.text.error
                            }`}>
                              {yoy.rbi_change > 0 ? '+' : ''}{yoy.rbi_change || '-'}
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className={`${dynastyTheme.components.card.base} p-6 rounded-lg`}>
              <div className={`text-center py-12 ${dynastyTheme.classes.text.neutralLight}`}>
                <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <div className="text-sm">No year-over-year data available</div>
                <div className="text-xs mt-1 opacity-75">Player needs at least 2 seasons of data for year-over-year comparison</div>
                <div className="text-xs mt-1 opacity-50">This will update once more season data is available</div>
              </div>
            </div>
          )}

          {/* Career Trajectory */}
          <div className={`${dynastyTheme.components.card.interactive} p-6 rounded-lg`}>
            <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4 flex items-center`}>
              <TrendingUp className="w-5 h-5 mr-2" />
              Career Development Arc
            </h4>
            
            {career_stats && career_stats.length >= 3 ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className={`${dynastyTheme.classes.bg.darkFlat} p-4 rounded`}>
                  <div className={`text-sm ${dynastyTheme.classes.text.success} mb-2 font-semibold`}>
                    BREAKOUT YEARS
                  </div>
                  {career_stats.length > 0 ? (
                    <ul className="space-y-2">
                      {career_stats
                        .slice()
                        .sort((a, b) => {
                          // Sort by best performance (highest AVG for hitters, lowest ERA for pitchers)
                          const aStat = isPitcher 
                            ? (a.pitching?.era || 999) 
                            : (a.hitting?.batting_avg || 0);
                          const bStat = isPitcher 
                            ? (b.pitching?.era || 999) 
                            : (b.hitting?.batting_avg || 0);
                          return isPitcher ? aStat - bStat : bStat - aStat;
                        })
                        .slice(0, 3)
                        .map(season => {
                          const stat = isPitcher
                            ? `${formatERA(season.pitching?.era) || 'N/A'} ERA`
                            : `${formatBattingAvg(season.hitting?.batting_avg) || '.000'} AVG`;
                          return (
                            <li key={season.season} className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                              • {season.season}: {stat}
                            </li>
                          );
                        })}
                    </ul>
                  ) : (
                    <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                      Insufficient data
                    </div>
                  )}
                </div>
                
                <div className={`${dynastyTheme.classes.bg.darkFlat} p-4 rounded`}>
                  <div className={`text-sm ${dynastyTheme.classes.text.primary} mb-2 font-semibold`}>
                    PEAK PERFORMANCE
                  </div>
                  <ul className="space-y-2">
                    <li className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                      • Best Season: {getCareerHigh(isPitcher ? 'wins' : 'home_runs')?.year || 'N/A'}
                    </li>
                    <li className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                      • Most Consistent: {getCareerHigh(isPitcher ? 'quality_starts' : 'batting_avg')?.year || 'N/A'}
                    </li>
                    <li className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                      • Award Years: {career_stats.length >= 5 ? 'See milestones tab' : 'N/A'}
                    </li>
                  </ul>
                </div>
                
                <div className={`${dynastyTheme.classes.bg.darkFlat} p-4 rounded`}>
                  <div className={`text-sm ${dynastyTheme.classes.text.warning} mb-2 font-semibold`}>
                    CURRENT PHASE
                  </div>
                  <ul className="space-y-2">
                    <li className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                      • Career Stage: {career_stats.length > 10 ? 'Veteran' : career_stats.length > 5 ? 'Prime' : 'Rising'}
                    </li>
                    <li className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                      • Trend: {year_over_year && year_over_year.length > 0 
                        ? (isPitcher 
                          ? (year_over_year[0].era_change < 0 ? 'Improving' : 'Stabilizing')
                          : (year_over_year[0].ops_change > 0 ? 'Improving' : 'Stabilizing'))
                        : 'Insufficient data'}
                    </li>
                    <li className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
                      • Projection: {career_stats.length >= 5 ? 'Based on career trends' : 'Needs more data'}
                    </li>
                  </ul>
                </div>
              </div>
            ) : (
              <div className={`text-center py-8 ${dynastyTheme.classes.text.neutralLight}`}>
                <TrendingUp className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <div className="text-sm">Insufficient career data</div>
                <div className="text-xs mt-1 opacity-75">Player needs at least 3 seasons of data for career development analysis</div>
              </div>
            )}
          </div>

          {/* Historical Player Similarity */}
          <div className={`${dynastyTheme.components.card.base} p-6 rounded-lg`}>
            <h4 className={`text-lg font-bold ${dynastyTheme.classes.text.primary} mb-4 flex items-center`}>
              <GitCompare className="w-5 h-5 mr-2" />
              Similar Historical Players
            </h4>
            
            {career_stats && career_stats.length >= 5 ? (
              <div className={`text-center py-8 ${dynastyTheme.classes.text.neutralLight}`}>
                <GitCompare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <div className="text-sm">Player similarity analysis coming soon</div>
                <div className="text-xs mt-1 opacity-75">This feature requires comprehensive career data analysis</div>
              </div>
            ) : (
              <div className={`text-center py-8 ${dynastyTheme.classes.text.neutralLight}`}>
                <GitCompare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <div className="text-sm">Insufficient data for similarity analysis</div>
                <div className="text-xs mt-1 opacity-75">Player needs at least 5 seasons of data</div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PlayerHistoricalAnalytics;